// The roster as a dock (PLAN-2026-10-17 §2.3, B-400): what keeps My Team
// recognizable while a tool takes the room beside it, and nothing more.
//
// SLOT AND NAME, AND THE MARKS FOR A SLOT THAT CANNOT SCORE — empty, a bye,
// Out — and the lock. No figures at any detail level: the dock is the
// roster's edge, not another list to read, so it never becomes a third
// column (U21, two columns and not three).
//
// TWO ACTIONS. A name opens its card in the tool beside, as the lineup's do;
// a slot chooses that slot, and the tool lists who can fill it (B-320).

import type { LineupRowView } from '@fsp/domain';
import { Icon } from '../../kit/Icon';
import { positionLabel, ProTeamCode } from '../../kit';

/** Why a starter's slot cannot score, in the fewest words that are still true. */
function cannotScore(r: LineupRowView): string | null {
  if (r.slotKind !== 'starter') return null;
  if (r.health === 'empty' || r.player === null) return 'Empty';
  if (r.health === 'bye') return 'Bye';
  if (r.health === 'out') return 'Out';
  return null;
}

export function RosterDock({
  starters,
  bench,
  reserve,
  onName,
  onSlot,
  chosen,
}: {
  starters: LineupRowView[];
  bench: LineupRowView[];
  reserve: LineupRowView[];
  /** A name: its card, in the tool beside. */
  onName: (r: LineupRowView) => void;
  /** A slot: who can fill it, or null where the roster cannot change. */
  onSlot: ((r: LineupRowView) => void) | null;
  /** The slot chosen now, drawn pressed. */
  chosen: (r: LineupRowView) => boolean;
}) {
  const row = (r: LineupRowView) => {
    const mark = cannotScore(r);
    return (
      <li key={`${r.slot}-${r.slotIndex}`} className="fsp-dock-row" data-mark={mark ?? undefined}>
        {onSlot !== null ? (
          <button
            type="button"
            className="fsp-dock-slot"
            aria-pressed={chosen(r)}
            // Its name says the slot as it reads on screen (DEF, not DST).
            aria-label={`Who can fill ${positionLabel(r.slot)}`}
            onClick={() => onSlot(r)}
          >
            {positionLabel(r.slot)}
          </button>
        ) : (
          <span className="fsp-dock-slot">{positionLabel(r.slot)}</span>
        )}
        {r.player !== null ? (
          <button
            type="button"
            className="fsp-linklike fsp-name-link fsp-dock-name"
            onClick={() => onName(r)}
            title={`${r.player.name}'s card`}
            aria-label={
              r.player.shortName !== r.player.name
                ? `${r.player.shortName}: ${r.player.name}`
                : undefined
            }
          >
            {r.player.shortName}
          </button>
        ) : (
          <span className="fsp-dock-name fsp-sub">Empty</span>
        )}
        <span className="fsp-dock-marks">
          {r.player !== null ? <ProTeamCode code={r.player.proTeam} /> : null}
          {mark !== null && r.player !== null ? (
            <span className="fsp-dock-mark">{mark}</span>
          ) : null}
          {r.locked && r.player !== null ? (
            <span className="fsp-dock-lock" title="Locked">
              <Icon name="locked" size="xs" />
              <span className="fsp-sr">Locked</span>
            </span>
          ) : null}
        </span>
      </li>
    );
  };
  return (
    <div className="fsp-dock">
      <ul className="fsp-dock-list" aria-label="Starters">
        {starters.map(row)}
      </ul>
      <div className="fsp-dock-head fsp-cap">Bench</div>
      <ul className="fsp-dock-list" aria-label="Bench">
        {bench.map(row)}
      </ul>
      {reserve.length > 0 ? (
        <>
          <div className="fsp-dock-head fsp-cap">Reserve</div>
          <ul className="fsp-dock-list" aria-label="Reserve">
            {reserve.map(row)}
          </ul>
        </>
      ) : null}
    </div>
  );
}
